import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { assessmentsApi, patientsApi, invoicesApi } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import toast from 'react-hot-toast';
import { HiOutlinePlus, HiOutlineUpload, HiOutlineDocumentText, HiPencil, HiEye, HiX, HiOutlineUserAdd, HiOutlinePhone, HiOutlineMail, HiOutlineSearch, HiOutlineCurrencyRupee, HiTrash } from 'react-icons/hi';
import AssessmentForm from '../../components/Forms/AssessmentForm';
import AssessmentDocumentsBlock from '../../components/Assessments/AssessmentDocumentsBlock';
import TherapyPlanForm from '../../components/Forms/TherapyPlanForm';
import TruncatedName from '../../components/TruncatedName';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { formatCalendarDateDMY, formatDateForInputIST } from '../../utils/timezoneDisplay';
import { INLINE_FILTER_BAR, INLINE_FILTER_FIELD } from '../../utils/inlineListFilters';

const Assessments = () => {
  const { canCreate, canUpdate, canDelete, isAdmin, canViewPricing } = usePermissions();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patient_id');
  const focusId = searchParams.get('focusId');
  const deepSearch = searchParams.get('search');
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailAssessment, setDetailAssessment] = useState(null);
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [assessmentToConvert, setAssessmentToConvert] = useState(null);
  const [showPatientDetail, setShowPatientDetail] = useState(false);
  const [detailPatient, setDetailPatient] = useState(null);
  const [showTherapyPlanForm, setShowTherapyPlanForm] = useState(false);
  const [newlyCreatedPatient, setNewlyCreatedPatient] = useState(null);
  const [uploadingFileId, setUploadingFileId] = useState(null);
  const [deletingAssessmentId, setDeletingAssessmentId] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const fileInputRefs = useRef({});
  const [filters, setFilters] = useState({
    search: '',
    doctor_id: '',
    status: '',
    date_from: '',
    date_to: '',
    page: 1
  });
  const [pagination, setPagination] = useState({});
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  useEffect(() => {
    if (deepSearch) {
      setFilters((prev) => ({ ...prev, search: deepSearch, page: 1 }));
    }
  }, [deepSearch]);

  useEffect(() => {
    fetchDoctors();
    fetchAssessments();

    // Check if coming from lead page with lead data
    if (location.state?.fromLead && location.state?.leadData) {
      setLeadData(location.state.leadData);
      setShowForm(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, patientId, debouncedSearch, filters.doctor_id, filters.status, filters.date_from, filters.date_to, filters.page]);

  const fetchDoctors = async () => {
    try {
      const response = await assessmentsApi.getDoctorUsers();
      setDoctors(response.data.data || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const params = {
        ...(patientId ? { patient_id: patientId } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.doctor_id ? { doctor_id: filters.doctor_id } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        page: filters.page,
        limit: 20
      };
      const response = await assessmentsApi.getAll(params);
      const allAssessments = response.data.data.assessments || [];
      const primaryAssessments = allAssessments.filter(
        (assessment) => !assessment.is_reassessment
      );
      setAssessments(primaryAssessments);
      setPagination(response.data.data.pagination || {});
    } catch (error) {
      toast.error('Failed to fetch assessments');
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value, page: 1 });
  };

  const clearFilters = () => {
    setFilters({ search: '', doctor_id: '', status: '', date_from: '', date_to: '', page: 1 });
  };

  const hasActiveFilters =
    filters.search || filters.doctor_id || filters.status || filters.date_from || filters.date_to;

  const handleCreate = () => {
    setSelectedAssessment(null);
    setShowForm(true);
  };

  const handleEdit = async (assessment) => {
    try {
      const response = await assessmentsApi.getById(assessment.id);
      setSelectedAssessment(response.data.data);
      setShowForm(true);
    } catch (error) {
      toast.error('Failed to load assessment details');
    }
  };

  const handleDelete = async (assessment) => {
    if (!assessment?.id) return;
    const label = assessment.is_reassessment ? 're-assessment' : 'assessment';
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    setDeletingAssessmentId(assessment.id);
    try {
      await assessmentsApi.delete(assessment.id);
      toast.success('Assessment deleted');
      if (detailAssessment && String(detailAssessment.id) === String(assessment.id)) {
        setShowDetail(false);
        setDetailAssessment(null);
      }
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete assessment');
    } finally {
      setDeletingAssessmentId(null);
    }
  };

  const handleView = async (assessment) => {
    try {
      const response = await assessmentsApi.getById(assessment.id);
      setDetailAssessment(response.data.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Failed to load assessment details');
    }
  };

  const handleFormSuccess = () => {
    fetchAssessments();
  };

  const handleConvert = async (assessment) => {
    try {
      const response = await assessmentsApi.getById(assessment.id);
      setAssessmentToConvert(response.data.data);
      setShowConvertForm(true);
    } catch (error) {
      toast.error('Failed to load assessment details');
    }
  };

  const handleConvertSubmit = async (patientData) => {
    try {
      const response = await assessmentsApi.convert(assessmentToConvert.id, patientData);
      toast.success('Assessment converted to patient successfully!');
      
      // Capture the newly created patient from response
      const createdPatient = response.data.data;
      
      // Close convert form
      setShowConvertForm(false);
      setAssessmentToConvert(null);
      
      // Set newly created patient and open therapy plan form
      setNewlyCreatedPatient(createdPatient);
      setShowTherapyPlanForm(true);
      
      // Refresh assessments to show updated status
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to convert assessment to patient');
    }
  };

  const handlePatientClick = async (patientId) => {
    if (!patientId) return;
    try {
      const response = await patientsApi.getById(patientId);
      setDetailPatient(response.data.data);
      setShowPatientDetail(true);
    } catch (error) {
      toast.error('Failed to load patient details');
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      const response = await invoicesApi.getById(invoiceId);
      setDetailInvoice(response.data.data);
      setShowInvoiceDetail(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleDocumentUpload = async (e, assessmentId) => {
    const file = e.target.files[0];
    if (!file) {
      e.target.value = '';
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp'
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, DOC, DOCX, and image files are allowed');
      e.target.value = '';
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      e.target.value = '';
      return;
    }

    setUploadingFileId(assessmentId);
    const formData = new FormData();
    formData.append('document', file);

    try {
      await assessmentsApi.uploadDocument(assessmentId, formData);
      toast.success('Document uploaded successfully');
      fetchAssessments();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload document';
      toast.error(errorMessage);
    } finally {
      setUploadingFileId(null);
      // Reset file input
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="font-display text-2xl font-bold text-gray-900">Assessments</h1>
        <p className="mt-0.5 text-sm text-gray-500">Patient assessments and diagnosis records</p>
      </div>

      <div className={INLINE_FILTER_BAR}>
        <div className="relative min-w-[9rem] flex-1 max-w-[14rem]">
          <HiOutlineSearch className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            placeholder="Search…"
            className={`${INLINE_FILTER_FIELD} w-full pl-8`}
          />
        </div>
        <select
          value={filters.doctor_id}
          onChange={(e) => handleFilterChange('doctor_id', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[6.5rem] max-w-[9rem]`}
          aria-label="Doctor"
        >
          <option value="">All doctors</option>
          {doctors.map((doctor) => (
            <option key={doctor.id} value={doctor.id}>
              {doctor.full_name}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[6.5rem] max-w-[8.5rem]`}
          aria-label="Status"
        >
          <option value="">All status</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => handleFilterChange('date_from', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
          aria-label="From date"
        />
        <span className="text-xs text-gray-400">–</span>
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => handleFilterChange('date_to', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
          min={filters.date_from || undefined}
          aria-label="To date"
        />
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="whitespace-nowrap px-1 text-xs font-medium text-gray-500 hover:text-gray-800"
          >
            Clear
          </button>
        ) : null}
      </div>

      <div className="card">
        {loading && !initialLoaded ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : assessments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <HiOutlineDocumentText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>No assessments found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Patient</th>
                  <th className="table-header">Lead</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Doctor</th>
                  <th className="table-header">Status</th>
                  {canViewPricing && <th className="table-header">Payment Status</th>}
                  <th className="table-header">Document</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assessments.map((assessment) => (
                  <tr
                    key={assessment.id}
                    className={`hover:bg-gray-50 ${String(assessment.id) === String(focusId) ? 'bg-primary-50' : ''}`}
                  >
                    <td className="table-cell">
                      {assessment.patient_name ? (
                        <>
                          <TruncatedName name={assessment.patient_name} maxLength={20}>
                            <button
                              onClick={() => handlePatientClick(assessment.patient_id)}
                              className="font-medium text-primary-600 hover:text-primary-700 hover:underline cursor-pointer"
                            />
                          </TruncatedName>
                          <p className="text-sm text-gray-500">{assessment.patient_code}</p>
                        </>
                      ) : (
                        <span className="text-gray-400 italic">No patient assigned</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {assessment.lead_name ? (
                        <>
                          <TruncatedName name={assessment.lead_name} maxLength={20}>
                            <Link 
                              to={`/leads/${assessment.lead_id}`}
                              className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                            />
                          </TruncatedName>
                          {assessment.lead_contact && (
                            <p className="text-sm text-gray-500">{assessment.lead_contact}</p>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {assessment.is_reassessment ? (
                        <span className="badge bg-blue-100 text-blue-700 mr-2">Re-Assessment</span>
                      ) : null}
                      {formatCalendarDateDMY(assessment.assessment_date)}
                    </td>
                    <td className="table-cell">
                      {assessment.doctor_name ? (
                        assessment.doctor_name
                      ) : (
                        <span className="text-gray-400 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${
                        assessment.status === 'completed' 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {assessment.status}
                      </span>
                    </td>
                    {canViewPricing && (
                    <td className="table-cell">
                      {assessment.invoice ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewInvoice(assessment.invoice.id)}
                              className="text-primary-600 hover:text-primary-700 hover:underline text-sm font-medium cursor-pointer"
                            >
                              {assessment.invoice.invoice_number}
                            </button>
                            <span className={`badge ${
                              assessment.invoice.status === 'paid'
                                ? 'bg-green-100 text-green-700'
                                : assessment.invoice.status === 'partial'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {assessment.invoice.status}
                            </span>
                          </div>
                          {assessment.invoice.balance_amount > 0 && (
                            <p className="text-xs text-gray-500">
                              Balance: ₹{parseFloat(assessment.invoice.balance_amount).toFixed(2)}
                            </p>
                          )}
                          {assessment.invoice.status === 'paid' && (
                            <p className="text-xs text-green-600 font-medium">
                              Paid: ₹{parseFloat(assessment.invoice.paid_amount).toFixed(2)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic text-sm">No invoice</span>
                      )}
                    </td>
                    )}
                    <td className="table-cell">
                      {/* Hidden file input for each assessment */}
                      <input
                        ref={(el) => {
                          if (el) {
                            fileInputRefs.current[assessment.id] = el;
                          }
                        }}
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => handleDocumentUpload(e, assessment.id)}
                        className="hidden"
                        disabled={uploadingFileId === assessment.id}
                      />
                      
                      {assessment.document_file_path ? (
                        <a
                          href={assessment.document_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 hover:underline text-sm flex items-center gap-1"
                        >
                          <HiOutlineDocumentText className="w-4 h-4" />
                          View Document
                        </a>
                      ) : (
                        <button
                          onClick={() => {
                            const fileInput = fileInputRefs.current[assessment.id];
                            if (fileInput) {
                              fileInput.click();
                            }
                          }}
                          disabled={uploadingFileId === assessment.id}
                          className="btn btn-secondary text-xs py-1 px-2 disabled:opacity-50"
                        >
                          <HiOutlineUpload className="w-4 h-4 mr-1" />
                          {uploadingFileId === assessment.id ? 'Uploading...' : 'Upload'}
                        </button>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleView(assessment)}
                          className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
                        >
                          <HiEye className="w-4 h-4" />
                          View
                        </button>
                        {canUpdate('assessments') && (
                        <button
                          onClick={() => handleEdit(assessment)}
                          className="text-green-600 hover:text-green-700 text-sm flex items-center gap-1"
                        >
                          <HiPencil className="w-4 h-4" />
                          Edit
                        </button>
                        )}
                        {canDelete('assessments') && (
                          <button
                            onClick={() => handleDelete(assessment)}
                            disabled={deletingAssessmentId === assessment.id}
                            className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1 disabled:opacity-50"
                          >
                            <HiTrash className="w-4 h-4" />
                            {deletingAssessmentId === assessment.id ? 'Deleting…' : 'Delete'}
                          </button>
                        )}
                        {!assessment.patient_id && canCreate('patients') && (
                          <button
                            onClick={() => handleConvert(assessment)}
                            className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                            title="Convert to Patient"
                          >
                            <HiOutlineUserAdd className="w-4 h-4" />
                            Convert
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={pagination.page === 1}
                className="btn btn-secondary text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={pagination.page >= pagination.totalPages}
                className="btn btn-secondary text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Assessment Form Modal */}
      {showForm && (
        <AssessmentForm
          assessment={selectedAssessment}
          leadData={leadData}
          onClose={() => {
            setShowForm(false);
            setSelectedAssessment(null);
            setLeadData(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Assessment Detail Modal */}
      {showDetail && detailAssessment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Assessment Details</h3>
              <button
                onClick={() => {
                  setShowDetail(false);
                  setDetailAssessment(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Patient</label>
                  {detailAssessment.patient_name ? (
                    <p className="text-gray-900 font-medium">{detailAssessment.patient_name} ({detailAssessment.patient_code})</p>
                  ) : (
                    <p className="text-gray-400 italic">No patient assigned</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Assessment Date</label>
                  <p className="text-gray-900">{formatCalendarDateDMY(detailAssessment.assessment_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Type</label>
                  <p className="text-gray-900">{detailAssessment.is_reassessment ? 'Re-Assessment' : 'Assessment'}</p>
                </div>
                {detailAssessment.is_reassessment ? (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Home Plan</label>
                    <p className="text-gray-900">{detailAssessment.give_home_plan ? 'Yes' : 'No'}</p>
                  </div>
                ) : null}
                {detailAssessment.reassessment_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Re-Assessment Date</label>
                    <p className="text-gray-900">{formatCalendarDateDMY(detailAssessment.reassessment_date)}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Assigned Doctor</label>
                  {detailAssessment.doctor_name ? (
                    <p className="text-gray-900">{detailAssessment.doctor_name}</p>
                  ) : (
                    <p className="text-gray-400 italic">Not assigned</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <span className={`badge ${
                    detailAssessment.status === 'completed' 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {detailAssessment.status}
                  </span>
                </div>
                {detailAssessment.therapy_suggestion && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Therapy Suggestion</label>
                    <p className="text-gray-900">{detailAssessment.therapy_suggestion}</p>
                  </div>
                )}
              </div>

              {/* Chief Complaints */}
              {detailAssessment.chief_complaints && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Chief Complaints</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.chief_complaints}</p>
                </div>
              )}

              {/* Medical History */}
              {detailAssessment.medical_history && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Medical History</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.medical_history}</p>
                </div>
              )}

              {/* Observations */}
              {detailAssessment.observations && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Observations</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.observations}</p>
                </div>
              )}

              {/* Recommendations */}
              {detailAssessment.recommendations && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Recommendations</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.recommendations}</p>
                </div>
              )}

              {/* Diagnoses */}
              {detailAssessment.diagnoses && detailAssessment.diagnoses.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Diagnoses</label>
                  <div className="mt-2 space-y-2">
                    {detailAssessment.diagnoses.map((diag, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium text-sm">
                          {diag.code ? `${diag.code} - ` : ''}{diag.diagnosis_name || diag.custom_diagnosis}
                        </p>
                        {diag.notes && <p className="text-xs text-gray-500 mt-1">{diag.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fee Information */}
              {canViewPricing && !detailAssessment.is_reassessment && detailAssessment.fee !== undefined && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Fee Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Assessment Fee</label>
                      <p className="text-gray-900">₹{parseFloat(detailAssessment.fee || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Fee Status</label>
                      <p className="text-gray-900">
                        {detailAssessment.fee_waived ? (
                          <span className="badge bg-amber-100 text-amber-700">Waived</span>
                        ) : (
                          <span className="badge bg-green-100 text-green-700">Not Waived</span>
                        )}
                      </p>
                    </div>
                    {detailAssessment.fee_waived && detailAssessment.waiver_reason && (
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-500">Waiver Reason</label>
                        <p className="text-gray-900">{detailAssessment.waiver_reason}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {detailAssessment.notes && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="text-sm font-medium text-gray-500">Additional Notes</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailAssessment.notes}</p>
                </div>
              )}
              {detailAssessment.give_home_plan ? (
                <div className="border-t border-gray-200 pt-4">
                  <label className="text-sm font-medium text-gray-500">Suggestion</label>
                  <div className="text-gray-900 mt-2 prose prose-sm max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: detailAssessment.home_plan_text || '' }} />
                  </div>
                </div>
              ) : null}
              <AssessmentDocumentsBlock assessment={detailAssessment} />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowDetail(false);
                  setDetailAssessment(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Patient Form Modal */}
      {showConvertForm && assessmentToConvert && (
        <ConvertToPatientForm
          assessment={assessmentToConvert}
          onClose={() => {
            setShowConvertForm(false);
            setAssessmentToConvert(null);
          }}
          onSubmit={handleConvertSubmit}
        />
      )}

      {/* Therapy Plan Form Modal - Opens after patient creation */}
      {showTherapyPlanForm && newlyCreatedPatient && (
        <TherapyPlanForm
          patient={newlyCreatedPatient}
          onClose={() => {
            setShowTherapyPlanForm(false);
            setNewlyCreatedPatient(null);
          }}
          onSuccess={() => {
            setShowTherapyPlanForm(false);
            setNewlyCreatedPatient(null);
            fetchAssessments();
          }}
        />
      )}

      {/* Patient Detail Modal */}
      {showPatientDetail && detailPatient && isAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Patient Details</h3>
              <button
                onClick={() => {
                  setShowPatientDetail(false);
                  setDetailPatient(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{detailPatient.full_name}</h4>
                    <p className="text-primary-600 mt-1">{detailPatient.patient_code}</p>
                  </div>
                  <span className={`badge ${
                    detailPatient.status === 'active' 
                      ? 'bg-green-100 text-green-700'
                      : detailPatient.status === 'discharged'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {detailPatient.status}
                  </span>
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-2 gap-6">
                <div className="card">
                  <h5 className="font-semibold text-gray-900 mb-4">Personal Information</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Age</span>
                      <span className="font-medium">{detailPatient.calculated_age || detailPatient.age || '-'} years</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Gender</span>
                      <span className="font-medium capitalize">{detailPatient.gender || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Date of Birth</span>
                      <span className="font-medium">
                        {detailPatient.dob ? (() => {
                          // Handle date string directly to avoid timezone issues
                          const dobStr = typeof detailPatient.dob === 'string' 
                            ? detailPatient.dob.split('T')[0] 
                            : formatDateForInputIST(detailPatient.dob);
                          // Format as DD-MM-YYYY for display
                          const [year, month, day] = dobStr.split('-');
                          return `${day}-${month}-${year}`;
                        })() : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Enrollment Date</span>
                      <span className="font-medium">
                        {detailPatient.enrollment_date ? formatCalendarDateDMY(detailPatient.enrollment_date) : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h5 className="font-semibold text-gray-900 mb-4">Contact Information</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Primary Contact</span>
                      <a href={`tel:${detailPatient.contact_primary}`} className="font-medium text-primary-600 hover:underline flex items-center gap-1">
                        <HiOutlinePhone className="w-4 h-4" />
                        {detailPatient.contact_primary}
                      </a>
                    </div>
                    {detailPatient.contact_whatsapp && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">WhatsApp</span>
                        <a href={`tel:${detailPatient.contact_whatsapp}`} className="font-medium text-primary-600 hover:underline">
                          {detailPatient.contact_whatsapp}
                        </a>
                      </div>
                    )}
                    {detailPatient.email && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Email</span>
                        <a href={`mailto:${detailPatient.email}`} className="font-medium text-primary-600 hover:underline flex items-center gap-1">
                          <HiOutlineMail className="w-4 h-4" />
                          {detailPatient.email}
                        </a>
                      </div>
                    )}
                    {detailPatient.address && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Address</span>
                        <span className="font-medium text-right max-w-xs">{detailPatient.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Guardian Information */}
              {(detailPatient.guardian_name || detailPatient.guardian_phone) && (
                <div className="card">
                  <h5 className="font-semibold text-gray-900 mb-4">Guardian Information</h5>
                  <div className="grid grid-cols-2 gap-4">
                    {detailPatient.guardian_name && (
                      <div>
                        <span className="text-gray-500">Guardian Name</span>
                        <p className="font-medium">{detailPatient.guardian_name}</p>
                      </div>
                    )}
                    {detailPatient.guardian_relation && (
                      <div>
                        <span className="text-gray-500">Relation</span>
                        <p className="font-medium capitalize">{detailPatient.guardian_relation}</p>
                      </div>
                    )}
                    {detailPatient.guardian_phone && (
                      <div>
                        <span className="text-gray-500">Guardian Phone</span>
                        <a href={`tel:${detailPatient.guardian_phone}`} className="font-medium text-primary-600 hover:underline">
                          {detailPatient.guardian_phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Medical Information */}
              {(detailPatient.allergies || detailPatient.emergency_contact) && (
                <div className="card">
                  <h5 className="font-semibold text-gray-900 mb-4">Medical Information</h5>
                  <div className="space-y-3">
                    {detailPatient.allergies && (
                      <div>
                        <span className="text-gray-500">Allergies</span>
                        <p className="font-medium mt-1">{detailPatient.allergies}</p>
                      </div>
                    )}
                    {detailPatient.emergency_contact && (
                      <div>
                        <span className="text-gray-500">Emergency Contact</span>
                        <a href={`tel:${detailPatient.emergency_contact}`} className="font-medium text-primary-600 hover:underline">
                          {detailPatient.emergency_contact}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {detailPatient.notes && (
                <div className="card">
                  <h5 className="font-semibold text-gray-900 mb-4">Notes</h5>
                  <p className="text-gray-700 whitespace-pre-wrap">{detailPatient.notes}</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPatientDetail(false);
                  setDetailPatient(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <Link
                to={`/patients/${detailPatient.id}`}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                View Full Profile
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceDetail && detailInvoice && canViewPricing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Invoice Details</h3>
              <button
                onClick={() => {
                  setShowInvoiceDetail(false);
                  setDetailInvoice(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Invoice Number</label>
                  <p className="text-gray-900 font-medium">{detailInvoice.invoice_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Invoice Date</label>
                  <p className="text-gray-900">{formatCalendarDateDMY(detailInvoice.invoice_date)}</p>
                </div>
                {detailInvoice.patient_name ? (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Patient</label>
                    <p className="text-gray-900 font-medium">{detailInvoice.patient_name} {detailInvoice.patient_code ? `(${detailInvoice.patient_code})` : ''}</p>
                  </div>
                ) : detailInvoice.assessment_date ? (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Assessment Date</label>
                    <p className="text-gray-900">{formatCalendarDateDMY(detailInvoice.assessment_date)}</p>
                  </div>
                ) : null}
                <div>
                  <label className="text-sm font-medium text-gray-500">Payment Type</label>
                  <p className="text-gray-900 capitalize">{detailInvoice.payment_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <span className={`badge ${
                    detailInvoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                    detailInvoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                    detailInvoice.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                    detailInvoice.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                    detailInvoice.status === 'cancelled' ? 'bg-gray-200 text-gray-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {detailInvoice.status}
                  </span>
                </div>
                {detailInvoice.due_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Due Date</label>
                    <p className="text-gray-900">{formatCalendarDateDMY(detailInvoice.due_date)}</p>
                  </div>
                )}
              </div>

              {detailInvoice.items && detailInvoice.items.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Items</label>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">Rate</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {detailInvoice.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{item.description}</td>
                            <td className="px-3 py-2 text-right">{item.quantity}</td>
                            <td className="px-3 py-2 text-right">₹{parseFloat(item.unit_price).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right">₹{parseFloat(item.amount).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">₹{parseFloat(detailInvoice.subtotal || 0).toFixed(2)}</span>
                  </div>
                  {detailInvoice.tax_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax:</span>
                      <span className="font-medium">₹{parseFloat(detailInvoice.tax_amount).toFixed(2)}</span>
                    </div>
                  )}
                  {detailInvoice.discount_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-red-600">-₹{parseFloat(detailInvoice.discount_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-300">
                    <span className="font-semibold">Total Amount:</span>
                    <span className="font-bold text-lg text-primary-600">₹{parseFloat(detailInvoice.total_amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid Amount:</span>
                    <span className="font-medium text-green-600">₹{parseFloat(detailInvoice.paid_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Balance:</span>
                    <span className="font-medium text-amber-600">₹{parseFloat(detailInvoice.balance_amount).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {detailInvoice.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-900 mt-1">{detailInvoice.notes}</p>
                </div>
              )}

              {detailInvoice.payments && detailInvoice.payments.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Payment History</label>
                  <div className="space-y-2">
                    {detailInvoice.payments.map((payment, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{payment.receipt_number}</span>
                          <span className="font-semibold text-green-600">₹{parseFloat(payment.amount).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{formatCalendarDateDMY(payment.payment_date)}</span>
                          <span className="capitalize">{payment.payment_mode}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowInvoiceDetail(false);
                  setDetailInvoice(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Convert to Patient Form Component
const ConvertToPatientForm = ({ assessment, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    dob: '',
    gender: '',
    address: '',
    contact_primary: '',
    contact_whatsapp: '',
    email: '',
    guardian_name: '',
    guardian_relation: '',
    guardian_phone: '',
    medical_history: '',
    emergency_contact: '',
    assigned_therapist_ids: []
  });
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);

  // Fetch doctors for dropdown
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await assessmentsApi.getDoctorUsers();
        setDoctors(response.data.data);
      } catch (error) {
        console.error('Failed to fetch doctors:', error);
      }
    };
    fetchDoctors();
  }, []);

  // Auto-fill from lead data if available
  useEffect(() => {
    if (assessment) {
      // Format DOB for date input (YYYY-MM-DD)
      // Backend now returns dates as 'YYYY-MM-DD' strings to avoid timezone issues
      let formattedDob = '';
      if (assessment.lead_dob) {
        // If it's already a string in YYYY-MM-DD format, use it directly
        // Otherwise, extract the date part (handles both Date objects and ISO strings)
        if (typeof assessment.lead_dob === 'string') {
          formattedDob = assessment.lead_dob.split('T')[0];
        } else {
          // Fallback for Date objects (shouldn't happen with our fix, but just in case)
          const dobDate = new Date(assessment.lead_dob);
          if (!isNaN(dobDate.getTime())) {
            // Use local date manipulation to avoid timezone issues
            const year = dobDate.getFullYear();
            const month = String(dobDate.getMonth() + 1).padStart(2, '0');
            const day = String(dobDate.getDate()).padStart(2, '0');
            formattedDob = `${year}-${month}-${day}`;
          }
        }
      }
      
      // Set form data with lead information
      const newFormData = {
        full_name: assessment.lead_name || assessment.lead_full_name || '',
        dob: formattedDob,
        gender: assessment.lead_gender || '',
        address: assessment.lead_address || '',
        contact_primary: assessment.lead_contact_primary || '',
        contact_whatsapp: assessment.lead_contact_whatsapp || '',
        email: assessment.lead_email || '',
        guardian_name: assessment.lead_guardian_name || '',
        guardian_relation: '',
        guardian_phone: '',
        medical_history: assessment.medical_history || assessment.patient_medical_history || '',
        emergency_contact: '',
        assigned_therapist_ids: []
      };
      
      setFormData(newFormData);
    }
  }, [assessment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTherapistToggle = (therapistId) => {
    const idStr = String(therapistId);
    setFormData((prev) => {
      const selected = prev.assigned_therapist_ids || [];
      return {
        ...prev,
        assigned_therapist_ids: selected.includes(idStr)
          ? selected.filter((id) => id !== idStr)
          : [...selected, idStr]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.contact_primary) {
      toast.error('Full name and primary contact are required');
      return;
    }
    if (!formData.assigned_therapist_ids || formData.assigned_therapist_ids.length === 0) {
      toast.error('Please assign at least one therapist');
      return;
    }
    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">Convert Assessment to Patient</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Contact <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="contact_primary"
                value={formData.contact_primary}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp Contact
              </label>
              <input
                type="tel"
                name="contact_whatsapp"
                value={formData.contact_whatsapp}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Guardian Name
              </label>
              <input
                type="text"
                name="guardian_name"
                value={formData.guardian_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Guardian Relation
              </label>
              <input
                type="text"
                name="guardian_relation"
                value={formData.guardian_relation}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Father, Mother"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Guardian Phone
              </label>
              <input
                type="tel"
                name="guardian_phone"
                value={formData.guardian_phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emergency Contact
              </label>
              <input
                type="tel"
                name="emergency_contact"
                value={formData.emergency_contact}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical History
              </label>
              <textarea
                name="medical_history"
                value={formData.medical_history}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Assign Therapists */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign Therapists <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
              {doctors.map((doctor) => {
                const checked = (formData.assigned_therapist_ids || []).includes(String(doctor.id));
                return (
                  <label key={doctor.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleTherapistToggle(doctor.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>{doctor.full_name}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1">Select one or more therapists</p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Converting...' : 'Convert to Patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Assessments;

